import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ConnectionQuality } from 'livekit-client';

import { Icon } from './Icon';
import { SpeakingMeter } from './SpeakingMeter';
import { CopyLinkButton } from './CopyLinkButton';
import { formatTime, initials, participantsWord } from '../lib/format';
import { QUALITY_LABEL, type ChatMessage, type ParticipantView } from '../lib/livekit';
import './SidePanel.css';

export type PanelTab = 'people' | 'chat';

export function SidePanel({
  tab,
  code,
  participants,
  chat,
  onTab,
  onClose,
  onSend,
}: {
  tab: PanelTab;
  code: string;
  participants: ParticipantView[];
  chat: ChatMessage[];
  onTab: (tab: PanelTab) => void;
  onClose: () => void;
  onSend: (text: string) => void;
}) {
  return (
    <aside className="panel" aria-label={tab === 'people' ? 'Участники' : 'Чат встречи'}>
      <header className="panel__head">
        {/* Вкладки построены на форме ярлыка, примыкающего к нижней грани. */}
        <div className="panel__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'people'}
            className={`panel__tab ${tab === 'people' ? 'panel__tab--on' : ''}`}
            onClick={() => onTab('people')}
          >
            Участники
            <span className="panel__tab-count">{participants.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'chat'}
            className={`panel__tab ${tab === 'chat' ? 'panel__tab--on' : ''}`}
            onClick={() => onTab('chat')}
          >
            Чат
          </button>
        </div>

        <button type="button" className="panel__close" onClick={onClose} aria-label="Закрыть панель">
          <Icon name="close" size={18} />
        </button>
      </header>

      {tab === 'people' ? (
        <PeopleTab participants={participants} code={code} />
      ) : (
        <ChatTab chat={chat} onSend={onSend} />
      )}
    </aside>
  );
}

// ── Участники ────────────────────────────────────────────────────────

function PeopleTab({ participants, code }: { participants: ParticipantView[]; code: string }) {
  return (
    <>
      <div className="panel__body">
        <p className="panel__hint">
          {participants.length} {participantsWord(participants.length)} на встрече
        </p>

        <ul className="people">
          {participants.map((p) => (
            <li className="people__row" key={p.sid}>
              <span className={`people__avatar ${p.isSpeaking ? 'people__avatar--speaking' : ''}`}>
                {initials(p.name)}
              </span>

              <span className="people__name">
                {p.name}
                {p.isLocal && <span className="people__you"> · вы</span>}
              </span>

              <span className="people__state">
                {p.screenShareEnabled && (
                  <Icon name="screen" size={16} className="people__icon people__icon--on" />
                )}
                {(p.connectionQuality === ConnectionQuality.Poor ||
                  p.connectionQuality === ConnectionQuality.Lost) && (
                  <span title={QUALITY_LABEL[p.connectionQuality]}>
                    <Icon name="alert" size={16} className="people__icon people__icon--warn" />
                  </span>
                )}
                <Icon
                  name={p.cameraEnabled ? 'cam' : 'camOff'}
                  size={16}
                  className={`people__icon ${p.cameraEnabled ? '' : 'people__icon--off'}`}
                />
                <SpeakingMeter participant={p.raw} muted={!p.micEnabled} size={18} />
              </span>
            </li>
          ))}
        </ul>
      </div>

      <footer className="panel__foot">
        <CopyLinkButton code={code} label="Пригласить по ссылке" />
      </footer>
    </>
  );
}

// ── Чат ──────────────────────────────────────────────────────────────

function ChatTab({ chat, onSend }: { chat: ChatMessage[]; onSend: (text: string) => void }) {
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  // Прокручиваем к последнему сообщению при появлении нового.
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [chat.length]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
  }

  return (
    <>
      <div
        className="panel__body panel__body--chat"
        ref={listRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {chat.length === 0 ? (
          <p className="panel__empty">
            Сообщения видны только тем, кто сейчас на встрече, и не сохраняются после её окончания.
          </p>
        ) : (
          <ul className="chat">
            {chat.map((message) => (
              <li className={`chat__item ${message.own ? 'chat__item--own' : ''}`} key={message.id}>
                <div className="chat__meta">
                  <span className="chat__from">{message.own ? 'Вы' : message.from}</span>
                  <time className="chat__time">{formatTime(message.at)}</time>
                </div>
                <div className="chat__bubble">{message.text}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form className="panel__foot chat__form" onSubmit={submit}>
        <input
          className="input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Сообщение участникам"
          maxLength={1000}
          aria-label="Текст сообщения"
        />
        <button type="submit" className="btn btn--primary btn--icon" disabled={!draft.trim()}>
          <Icon name="send" size={18} />
          <span className="visually-hidden">Отправить</span>
        </button>
      </form>
    </>
  );
}
